import ButtonGroupActions, { Action } from '@/components/ButtonGroupActions'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getService } from '@/modules/Service/services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ErrorBoundary, Typogaphy } from 'vnetwork-ui'
import Templates from '../../Templates'
import ServiceUpdates from '../../Templates/shared/ServiceUpdates'
import InternalTicketWorkflow from './InternalTicketWorkflow'
import ServiceAdditional from './ServiceAdditional'
import PriceChangeAlert from './PriceChangeAlert'
import { useServiceQuickHelp } from './use-service-quick-help'
import { useServiceActions } from './use-service-actions'
import { Badge } from '@/components/ui/badge'
import { AlertCircle } from 'lucide-react'
import { DEFAULT_PAYMENT_TERM_DAYS } from '@/modules/Service/constants'

const Information = () => {
    const { id: serviceID } = useParams()
    const { data: service, renderState } = useQueryWithHandler({
        queryFn: () => getService(Number(serviceID)),
        queryKey: [QUERY_KEYS.SERVICE_DETAIL, Number(serviceID)],
    })

    const { t } = useTranslation()
    const [showMoreDetails, setShowMoreDetails] = useState(false)

    useServiceQuickHelp()

    const { serviceActions } = useServiceActions({
        service,
        serviceID,
        showMoreDetails,
        setShowMoreDetails,
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    if (!service) return null

    const ServiceTemplate =
        Templates[service?.service_template] || Templates['Blank']

    const Information =
        ServiceTemplate?.Information || Templates['Blank']?.Information

    return (
        <>
            <InternalTicketWorkflow
                serviceId={service.service_id}
                service={service}
            />
            {service?.customer_language &&
                service?.customer_language !== 'vi' && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-2 flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                        <div className="flex items-center gap-2 flex-wrap">
                            <Typogaphy className="text-sm text-orange-800 font-medium">
                                {t('non-vietnamese-language')}:
                            </Typogaphy>
                            <Badge
                                variant="default"
                                className="uppercase bg-orange-500 hover:bg-orange-600"
                            >
                                {service.customer_language}
                            </Badge>
                        </div>
                    </div>
                )}
            <PriceChangeAlert serviceID={service.service_id} />
            <ButtonGroupActions
                className="rounded-sm border border-gray-200"
                actions={
                    serviceActions?.filter(
                        (action) => !action?.hidden
                    ) as Action[]
                }
            />
            <div className="flex items-center gap-2 mt-2 px-1">
                <Typogaphy className="text-sm text-gray-500">
                    {t('services:service-creator')}:
                </Typogaphy>
                <Typogaphy className="text-sm font-medium text-gray-900">
                    {service.employee_create_name ||
                        t('services:service-creator-unknown')}
                </Typogaphy>
                <Typogaphy className="text-sm text-gray-500 ml-4">
                    {t('services:payment_term_days')}:
                </Typogaphy>
                <Typogaphy className="text-sm font-medium text-gray-900">
                    {t('services:payment_term_days_value', {
                        count:
                            service.payment_term_days ??
                            DEFAULT_PAYMENT_TERM_DAYS,
                    })}
                </Typogaphy>
            </div>
            {showMoreDetails && (
                <ServiceAdditional
                    service={service}
                    showMoreDetails={showMoreDetails}
                />
            )}
            <ErrorBoundary>
                <Information service={service} />
            </ErrorBoundary>
            <ServiceUpdates />
        </>
    )
}

export default Information
